import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sprout, Leaf, CalendarCheck, FlaskConical, BookOpen, TrendingUp, Droplets, Zap, Scale, Download } from 'lucide-react';
import WeatherWidget from '../components/WeatherWidget';

const API = process.env.REACT_APP_BACKEND_URL;

const PHASE_LABELS = {
  germination: 'Keimung',
  seedling: 'Sämling',
  vegetative: 'Vegetativ',
  flowering: 'Blüte',
  harvest: 'Ernte',
};

const CARE_ICONS = {
  watering: <Droplets size={14} />,
  fertilizing: <Zap size={14} />,
  training: <TrendingUp size={14} />,
};

const CARE_LABELS = {
  watering: 'Gegossen',
  fertilizing: 'Gedüngt',
  training: 'Trainiert',
  topping: 'Getoppt',
  defoliation: 'Entblättert',
  repotting: 'Umgetopft',
  other: 'Sonstiges',
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  // PWA-Install-Logik
  useEffect(() => {
    // Bereits installiert? Dann nicht anzeigen
    if (window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true) {
      return;
    }

    // iOS erkennen
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    setIsIOS(ios);
    if (ios) {
      setShowInstallBanner(true);
      return;
    }

    // Android/Desktop: beforeinstallprompt abwarten
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setShowInstallBanner(false));

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') setShowInstallBanner(false);
  };

  useEffect(() => {
    axios.get(`${API}/api/dashboard`)
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const phaseEntries = stats ? Object.entries(stats.phases ?? {}).filter(([, v]) => v > 0) : [];

  return (
    <div className="p-4 md:p-6 space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold text-emerald-50">Dashboard</h1>
          <p className="text-emerald-100/50 text-sm mt-1">Deine Grow-Übersicht</p>
        </div>
        <div className="w-full sm:w-72 lg:w-80">
          <WeatherWidget />
        </div>
      </div>

      {/* PWA Install Banner */}
      {showInstallBanner && (
        <div style={{ background: '#1e3a2f', border: '1px solid #4ade80', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}
          data-testid="install-banner"
        >
          <span style={{ fontSize: 24 }}>📲</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: '#4ade80' }}>App installieren</div>
            {isIOS
              ? <div style={{ fontSize: 13, color: '#9ca3af' }}>Tippe auf <strong>Teilen</strong> → <strong>Zum Home-Bildschirm</strong></div>
              : <div style={{ fontSize: 13, color: '#9ca3af' }}>Growbot2 als App auf deinem Gerät speichern</div>
            }
          </div>
          {!isIOS && (
            <button onClick={handleInstall} style={{ background: '#4ade80', color: '#000', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer' }}
              data-testid="install-btn"
            >
              Installieren
            </button>
          )}
          <button onClick={() => setShowInstallBanner(false)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="stats-grid">
        <StatCard icon={<Sprout size={20} />} label="Aktive Projekte" value={stats?.active_projects ?? 0} color="emerald" link="/projects" />
        <StatCard icon={<Leaf size={20} />} label="Pflanzen" value={stats?.total_plants ?? 0} color="green" link="/plants" />
        <StatCard icon={<FlaskConical size={20} />} label="Strains" value={stats?.strain_count ?? 0} color="teal" link="/strains" />
        <StatCard icon={<Scale size={20} />} label="Ernten" value={stats?.total_harvests ?? 0} color="yellow" link="/harvests" />
      </div>

      {/* Plants by Phase */}
      {phaseEntries.length > 0 && (
        <div className="card-grow p-4" data-testid="phase-overview">
          <h2 className="font-heading font-semibold text-emerald-100 mb-3 flex items-center gap-2">
            <Leaf size={16} className="text-emerald-400" />
            Pflanzen nach Phase
          </h2>
          <div className="flex flex-wrap gap-2">
            {phaseEntries.map(([phase, count]) => (
              <div key={phase} className={`badge badge-green`}>
                <span className={`phase-${phase}`}>{PHASE_LABELS[phase] || phase}</span>
                <span className="ml-1 text-emerald-100/60">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {/* Recent Care */}
        <div className="card-grow p-4" data-testid="recent-care">
          <h2 className="font-heading font-semibold text-emerald-100 mb-3 flex items-center gap-2">
            <CalendarCheck size={16} className="text-emerald-400" />
            Letzte Pflegevorgänge
          </h2>
          {stats?.recent_care?.length > 0 ? (
            <div className="space-y-2">
              {stats.recent_care.map(log => (
                <div key={log.id} className="flex items-center gap-3 py-2 border-b last:border-0" style={{ borderColor: '#1B2A21' }}>
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center text-emerald-400 flex-shrink-0"
                    style={{ background: 'rgba(16,185,129,0.1)' }}>
                    {CARE_ICONS[log.care_type] || <CalendarCheck size={14} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-emerald-100">{CARE_LABELS[log.care_type] || log.care_type}</p>
                    <p className="text-xs text-emerald-100/40 truncate">
                      {log.plant_ids?.length} Pflanze(n) · {new Date(log.performed_at).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState text="Noch keine Pflegevorgänge" link="/care-log" linkText="Pflege eintragen" />
          )}
        </div>

        {/* Recent Diary */}
        <div className="card-grow p-4" data-testid="recent-diary">
          <h2 className="font-heading font-semibold text-emerald-100 mb-3 flex items-center gap-2">
            <BookOpen size={16} className="text-emerald-400" />
            Letzte Tagebucheinträge
          </h2>
          {stats?.recent_diary?.length > 0 ? (
            <div className="space-y-2">
              {stats.recent_diary.map(entry => (
                <div key={entry.id} className="py-2 border-b last:border-0" style={{ borderColor: '#1B2A21' }}>
                  <p className="text-sm font-medium text-emerald-100 truncate">{entry.title}</p>
                  <p className="text-xs text-emerald-100/40 mt-0.5 line-clamp-1">{entry.content}</p>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState text="Noch keine Einträge" link="/diary" linkText="Eintrag erstellen" />
          )}
        </div>
      </div>

      {/* Harvest Summary on Dashboard */}
      {(stats?.total_harvests > 0) && (
        <div className="card-grow p-4" data-testid="harvest-summary">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-semibold text-emerald-100 flex items-center gap-2">
              <Scale size={16} className="text-yellow-400" />
              Ernte-Übersicht
            </h2>
            <Link to="/harvests" className="text-sm text-emerald-400 hover:text-emerald-300">Alle ansehen</Link>
          </div>
          <div className="flex gap-6">
            <div>
              <p className="font-heading text-xl font-bold text-emerald-50">{stats.total_harvests}</p>
              <p className="text-xs text-emerald-100/50">Ernten</p>
            </div>
            {stats.total_dry_weight_g > 0 && (
              <div>
                <p className="font-heading text-xl font-bold text-yellow-400">
                  {stats.total_dry_weight_g >= 1000
                    ? `${(stats.total_dry_weight_g / 1000).toFixed(2)} kg`
                    : `${stats.total_dry_weight_g.toFixed(1)} g`}
                </p>
                <p className="text-xs text-emerald-100/50">Trockengewicht gesamt</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="card-grow p-4" data-testid="quick-actions">
        <h2 className="font-heading font-semibold text-emerald-100 mb-3">Schnellzugriff</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { to: '/projects', label: 'Neues Projekt', icon: Sprout },
            { to: '/plants', label: 'Pflanze anlegen', icon: Leaf },
            { to: '/care-log', label: 'Pflege eintragen', icon: CalendarCheck },
            { to: '/diary', label: 'Tagebuch', icon: BookOpen },
          ].map(({ to, label, icon: Icon }) => (
            <Link key={to} to={to} className="flex flex-col items-center gap-2 p-3 rounded-xl text-center transition-colors hover:bg-[#111C15]"
              style={{ border: '1px solid #1B2A21' }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center text-emerald-400"
                style={{ background: 'rgba(16,185,129,0.1)' }}>
                <Icon size={18} />
              </div>
              <span className="text-xs text-emerald-100/70">{label}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color, link }) {
  return (
    <Link to={link} className="card-grow p-4 block" data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-emerald-400"
          style={{ background: 'rgba(16,185,129,0.12)' }}>
          {icon}
        </div>
      </div>
      <div className="mt-3">
        <p className="font-heading text-2xl font-bold text-emerald-50">{value}</p>
        <p className="text-xs text-emerald-100/50 mt-0.5">{label}</p>
      </div>
    </Link>
  );
}

function EmptyState({ text, link, linkText }) {
  return (
    <div className="text-center py-4">
      <p className="text-sm text-emerald-100/30">{text}</p>
      <Link to={link} className="text-sm text-emerald-400 hover:text-emerald-300 mt-1 inline-block">{linkText}</Link>
    </div>
  );
}
